import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  fetchRecycleBin,
  restoreRecycleBinItems,
  permanentDeleteRecycleBinItems,
  RecycleBinItem,
} from '../../store/actions/RecycleBinActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Table from '../shared/TailwindComponents/Table';
import {
  PiTrashLight,
  PiArrowCounterClockwiseLight,
  PiWarningCircleLight,
  PiRecycleFill,
} from 'react-icons/pi';
import { formatDateTime } from '../../utils/functions';
import Badge from '../shared/TailwindComponents/Badge';
import RecycleBinDeleteConfirmation from './RecycleBinDeleteConfirmation';

interface ResourceTrashProps {
  entityType: string;
  entityLabel: string;
  emptyIcon?: React.ReactElement;
  onItemRestored?: () => void;
  onBulkActionsChange?: (actions: { label: string; onClick: () => void; icon?: React.ReactElement }[]) => void;
  onSelectedCountChange?: (count: number) => void;
}

const ResourceTrash: React.FC<ResourceTrashProps> = ({
  entityType,
  entityLabel,
  emptyIcon,
  onItemRestored,
  onBulkActionsChange,
  onSelectedCountChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isAdmin = !!userInfo?.is_org_admin;

  const [items, setItems] = useState<RecycleBinItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('deleted_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  const loadItems = useCallback(async (page = 1, append = false) => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchRecycleBin({
        page,
        per_page: 50,
        entity_type: entityType,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        user_only: true,
      }) as any);

      if (result?.success) {
        const pageItems: RecycleBinItem[] = result.items || [];
        setItems(prev => append ? [...prev, ...pageItems] : pageItems);
        setRetentionDays(result.retention_days || 30);
        if (result.pagination) {
          setPagination(result.pagination);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, entityType, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const getItemKey = (item: RecycleBinItem): string => {
    return `${item.entity_type}:${item.uuid || item.id}`;
  };

  const handleRestore = async (itemsToRestore?: RecycleBinItem[]) => {
    const targets = itemsToRestore || items.filter(i => selectedItems.includes(getItemKey(i)));
    const restorePayload = targets.map(i => ({
      entity_type: i.entity_type,
      id: i.uuid || i.id,
    }));

    setIsLoading(true);
    try {
      const success = await dispatch(restoreRecycleBinItems(restorePayload) as any);
      if (success) {
        setSelectedItems([]);
        await loadItems(1);
        onItemRestored?.();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async (itemsToDelete?: RecycleBinItem[]) => {
    const targets = itemsToDelete || items.filter(i => selectedItems.includes(getItemKey(i)));
    const deletePayload = targets.map(i => ({
      entity_type: i.entity_type,
      id: i.uuid || i.id,
    }));

    dispatch(
      showModal({
        type: 'custom',
        message: t('Permanently Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        content: (
          <RecycleBinDeleteConfirmation
            itemCount={targets.length}
            onConfirm={async () => {
              setIsLoading(true);
              try {
                const success = await dispatch(permanentDeleteRecycleBinItems(deletePayload) as any);
                if (success) {
                  setSelectedItems([]);
                  await loadItems(1);
                }
              } finally {
                setIsLoading(false);
                dispatch(hideModal());
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true,
      })
    );
  };

  const expiringItems = items.filter(i => i.days_remaining <= 7 && i.days_remaining > 0);

  const columns = ['Select', 'Name', 'Created By', 'Deleted By', 'Deleted', 'Days Remaining', 'Actions'];

  const tableData = items.map((item) => ({
    id: getItemKey(item),
    doc: item,
    Select: null,
    Name: (
      <span className="truncate" title={item.name}>
        {item.name}
      </span>
    ),
    'Created By': item.created_by_name ? (
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-700 truncate">{item.created_by_name}</span>
        <Badge color={item.created_by_role === 'Admin' ? 'amber' : 'gray'} className="text-[10px] leading-tight px-1.5 py-0">
          {item.created_by_role}
        </Badge>
      </div>
    ) : (
      <span className="text-sm text-gray-400">—</span>
    ),
    'Deleted By': item.deleted_by_name ? (
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-700 truncate">{item.deleted_by_name}</span>
        <Badge color={item.deleted_by_role === 'Admin' ? 'amber' : 'gray'} className="text-[10px] leading-tight px-1.5 py-0">
          {item.deleted_by_role}
        </Badge>
      </div>
    ) : (
      <span className="text-sm text-gray-400">—</span>
    ),
    Deleted: formatDateTime(item.deleted_at),
    'Days Remaining': (
      <span className={`text-sm font-medium ${item.days_remaining <= 7 ? 'text-red-600' : 'text-gray-500'}`}>
        {item.days_remaining} {t('days')}
      </span>
    ),
  }));

  const tableActions = [
    {
      label: t('Restore'),
      onClick: (row: any) => handleRestore([row.doc]),
      icon: <PiArrowCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Permanently'),
      onClick: (row: any) => handlePermanentDelete([row.doc]),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const selectableIds = tableData.map(row => row.id);
  const handleSelectAllItems = () => {
    setSelectedItems(selectedItems.length === selectableIds.length ? [] : selectableIds);
  };

  const handleLoadMore = () => {
    if (pagination.has_next) {
      loadItems(pagination.page + 1, true);
    }
  };

  // Keep refs to latest handlers so parent's onClick closures are never stale
  const restoreRef = useRef(() => handleRestore());
  const deleteRef = useRef(() => handlePermanentDelete());
  restoreRef.current = () => handleRestore();
  deleteRef.current = () => handlePermanentDelete();

  useEffect(() => {
    if (!onBulkActionsChange) return;
    if (selectedItems.length > 0) {
      onBulkActionsChange([
        { label: t('Restore Selected'), onClick: () => restoreRef.current(), icon: <PiArrowCounterClockwiseLight className="h-4 w-4" /> },
        { label: t('Delete Selected'), onClick: () => deleteRef.current(), icon: <PiTrashLight className="h-4 w-4" /> },
      ]);
    } else {
      onBulkActionsChange([]);
    }
  }, [selectedItems, onBulkActionsChange, t]);

  useEffect(() => {
    onSelectedCountChange?.(selectedItems.length);
  }, [selectedItems.length, onSelectedCountChange]);

  return (
    <>
      {expiringItems.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm">
          <PiWarningCircleLight className="w-4 h-4 flex-shrink-0" />
          <span>
            {expiringItems.length} {expiringItems.length === 1 ? t('item') : t('items')} {t('will be permanently deleted within 7 days')}
          </span>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto relative">
        <div className="flex-1 overflow-x-auto overflow-y-auto">
          <Table
            isLoadingMore={isLoading}
            columns={columns}
            data={tableData}
            actions={tableActions}
            enableSelection
            selectedIds={selectedItems}
            onSelect={handleSelectItem}
            onSelectAll={handleSelectAllItems}
            hasMore={pagination.has_next}
            onLoadMore={handleLoadMore}
            resizableColumns={true}
            defaultColumnWidths={{
              'Select': 48,
              'Name': 300,
              'Deleted': 180,
              'Days Remaining': 130,
              'Actions': 48,
            }}
            sortableColumns={['Name', 'Deleted', 'Days Remaining']}
            sortBy={sortBy === 'deleted_at' ? 'deleted' : sortBy === 'days_remaining' ? 'days_remaining' : sortBy}
            sortOrder={sortOrder}
            onSort={(col, order) => {
              const columnMap: Record<string, string> = {
                'name': 'name',
                'deleted': 'deleted_at',
                'days_remaining': 'days_remaining',
              };
              setSortBy(columnMap[col] || col);
              setSortOrder(order);
            }}
            total_items={pagination.total_items}
            total_showing_items={tableData.length}
            max="max-h-[calc(100vh-150px)]"
            min="max-h-[calc(100vh-150px)]"
            setIsShowing={true}
            showingLabel={t('items')}
            infoText={`${t('Deleted')} ${t(entityLabel).toLowerCase()} ${t('are automatically removed after')} ${retentionDays} ${t('days.')}`}
            emptyState={{
              icon: emptyIcon || <PiRecycleFill />,
              headline: t(`No deleted ${entityLabel.toLowerCase()}`),
              subtext: t(`Deleted ${entityLabel.toLowerCase()} will appear here`),
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ResourceTrash;
